import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ARPagedResponseDataModel } from '@relayter/core';
import {
    INucDropdownRequestDataEvent,
    NUC_BUTTON_TYPE,
    NUC_DIALOG_CUSTOM_CONTENT_DATA,
    NucButtonConfig,
    NucDialogCustomContentService,
    NucDropdownComponent,
    NucDropdownItem,
    NucDropdownMultiSelectComponent,
    NucFormFieldComponent,
    NucFullModalActionModel,
    NucInputComponent
} from '@relayter/rubber-duck';
import { distinctUntilChanged, finalize, map, Subscription } from 'rxjs';
import { TeamMemberService } from 'src/app/api/services/team-member.service';
import { TeamMemberModel } from 'src/app/models/response/team-member.model';
import { RoleService } from '../../api/services/role.service';
import { TeamService } from '../../api/services/team.service';
import { AppConstants } from '../../app.constants';
import { Toaster } from '../../classes/toaster.class';
import { RoleModel } from '../../models/response/role.model';
import { TeamModel } from '../../models/response/team.model';

export interface ITransferOwnershipFormData {
    team: TeamModel;
}

class TransferOwnershipForm {
    newOwner: FormControl<string>;
    newRoles: FormControl<string[]>;
    confirmText: FormControl<string>;
}

@Component({
    selector: 'om-transfer-ownership-form',
    templateUrl: './transfer-ownership-form.component.html',
    styleUrl: './transfer-ownership-form.component.scss',
    imports: [NucDropdownComponent, NucDropdownMultiSelectComponent, NucInputComponent, ReactiveFormsModule, NucFormFieldComponent]
})
export class TransferOwnershipFormComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly teamMemberService = inject(TeamMemberService);
    private readonly roleService = inject(RoleService);
    private readonly teamService = inject(TeamService);
    private readonly dialogCustomContentService = inject(NucDialogCustomContentService);
    private readonly data = inject<ITransferOwnershipFormData>(NUC_DIALOG_CUSTOM_CONTENT_DATA);

    public team: TeamModel;
    public form: FormGroup<TransferOwnershipForm>;

    public roles: RoleModel[] = [];
    public teamMembers: NucDropdownItem<string>[] = [];
    public totalTeamMembers: number;
    public totalRoles: number;
    public pageSize = AppConstants.PAGE_SIZE_DEFAULT;

    private transferButton: NucButtonConfig;

    public teamMemberSubscription: Subscription;
    public rolesSubscription: Subscription;

    constructor() {
        this.team = this.data.team;
    }

    public ngOnInit(): void {
        this.getTeamMembers();
        this.getRoles();
        this.initModalButtons();
        this.initForm();
    }

    public initForm(): void {
        this.form = new FormGroup({
            newOwner: new FormControl(null, Validators.required),
            newRoles: new FormControl(null, Validators.required),
            confirmText: new FormControl(null, Validators.pattern(/^Confirm$/))
        });

        this.form.statusChanges
            .pipe(
                map((status) => status === 'VALID'),
                distinctUntilChanged()
            )
            .subscribe((valid) => {
                this.transferButton.disabled = !valid;
            });
    }

    public getTeamMembers(event?: INucDropdownRequestDataEvent): void {
        if (this.teamMemberSubscription && !this.teamMemberSubscription?.closed) {
            this.teamMemberSubscription.unsubscribe();
        }
        if (event?.reset) {
            this.teamMembers = [];
        }

        this.teamMemberSubscription = this.teamMemberService
            .getTeamMembers(this.team._id, [this.team.owner._id], event?.limit || this.pageSize, event?.offset, 'user.fullName', 'asc', event?.search)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: ARPagedResponseDataModel<TeamMemberModel>) => {
                    this.teamMembers = this.teamMembers.concat(
                        res.items.map((item) => new NucDropdownItem<string>(item.user.fullName, item.user._id, null, null, item.user.email))
                    );
                    this.totalTeamMembers = res.total;
                },
                error: Toaster.handleApiError
            });
    }

    public getRoles(event?: INucDropdownRequestDataEvent): void {
        if (this.rolesSubscription && !this.rolesSubscription?.closed) {
            this.rolesSubscription.unsubscribe();
        }
        if (event?.reset) {
            this.teamMembers = [];
            this.totalTeamMembers = undefined;
        }

        this.rolesSubscription = this.roleService
            .getRoles(this.team._id, event?.limit, event?.offset, 'name', 'asc', event?.search)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: ARPagedResponseDataModel<RoleModel>) => {
                    this.roles = this.roles.concat(res.items);
                    this.totalRoles = res.total;
                },
                error: Toaster.handleApiError
            });
    }

    public transferOwnership(): void {
        this.transferButton.loading = true;
        const body = {
            newOwnerId: this.form.value.newOwner,
            newRoleIds: this.form.value.newRoles
        };

        this.teamService
            .transferOwnership(this.team._id, body)
            .pipe(
                finalize(() => {
                    this.transferButton.loading = false;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    const newOwner = this.teamMembers.find((teamMember) => teamMember.getValue() === this.form.value.newOwner);
                    Toaster.success(`Team ownership has been transferred to ${newOwner?.getTitle() || 'Unknown user'}`);
                    this.dialogCustomContentService.close(true);
                },
                error: Toaster.handleApiError
            });
    }

    private initModalButtons(): void {
        this.transferButton = new NucButtonConfig(NUC_BUTTON_TYPE.PRIMARY, 'Confirm transfer', null, null, true);
        const transferAction = new NucFullModalActionModel(this.transferButton);
        const cancelAction = new NucFullModalActionModel(new NucButtonConfig(NUC_BUTTON_TYPE.SECONDARY, 'Cancel'));
        this.dialogCustomContentService.addDialogAction(cancelAction);
        this.dialogCustomContentService.addDialogAction(transferAction);

        cancelAction.observable.subscribe(() => this.dialogCustomContentService.close());
        transferAction.observable.subscribe(() => this.transferOwnership());
    }
}
